import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode'; // Ensure jwt-decode is installed
import axios from 'axios'; // Import axios for API calls

// 1. Create the Context
const AuthContext = createContext(null);

// Helper function to check if a string is a structurally valid JWT
function isValidJWT(token) {
  if (!token || typeof token !== 'string') {
    // console.log('isValidJWT: false - not a string or empty', token);
    return false;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    // console.log('isValidJWT: false - wrong number of parts', token);
    return false;
  }
  const isValid = parts.every(part => typeof part === 'string' && part.length > 0);
  // if (!isValid) console.log('isValidJWT: false - one or more parts are invalid', token);
  return isValid;
}

// Helper function to get initial state from localStorage
const getInitialState = () => {
  let token = localStorage.getItem('authToken');
  let adminToken = localStorage.getItem('adminToken');

  // Validate and clear if invalid
  if (token && !isValidJWT(token)) {
    console.warn('getInitialState: Invalid authToken found, clearing.', token);
    localStorage.removeItem('authToken');
    token = null;
  }
  if (adminToken && !isValidJWT(adminToken)) {
    console.warn('getInitialState: Invalid adminToken found, clearing.', adminToken);
    localStorage.removeItem('adminToken');
    adminToken = null;
  }

  let activeToken = token || adminToken; // Use authToken if present, else fallback to adminToken

  let currentUser = null;
  let isInPreviewMode = false;
  let previewStudentId = null;

  // Sync tokens if one is valid and the other is missing/different
  if (token && isValidJWT(token) && token !== adminToken) {
    console.log('getInitialState: Syncing adminToken with valid authToken');
    localStorage.setItem('adminToken', token);
    adminToken = token;
  } else if (adminToken && isValidJWT(adminToken) && adminToken !== token) {
    console.log('getInitialState: Syncing authToken with valid adminToken');
    localStorage.setItem('authToken', adminToken);
    activeToken = adminToken;
  }


  if (activeToken && isValidJWT(activeToken)) {
    try {
      const decoded = jwtDecode(activeToken);
      if (decoded.exp * 1000 < Date.now()) {
        console.log('Initial active token expired, clearing state.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('currentUserData');
        activeToken = null; // Nullify activeToken
        currentUser = null;
        isInPreviewMode = false;
        previewStudentId = null;
      } else {
        isInPreviewMode = decoded.is_in_preview_mode || false;
        previewStudentId = decoded.preview_student_id || null;

        const storedUserData = localStorage.getItem('currentUserData');
        if (storedUserData) {
          currentUser = JSON.parse(storedUserData);
          if (currentUser && (currentUser.id !== (decoded.user_id || decoded.student_id))) {
            console.warn('Mismatch between token and stored user data. Clearing stored data.');
            localStorage.removeItem('currentUserData');
            currentUser = null;
          }
        }
      }
    } catch (e) {
      console.error("Error decoding active token during init:", e, activeToken);
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('currentUserData');
      activeToken = null; // Nullify activeToken
      currentUser = null;
      isInPreviewMode = false;
      previewStudentId = null;
    }
  } else {
    // No valid active token, ensure everything is cleared
    if (activeToken) console.warn('getInitialState: activeToken was present but invalid, clearing everything.', activeToken)
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('currentUserData');
    activeToken = null;
    currentUser = null;
    isInPreviewMode = false;
    previewStudentId = null;
  }

  return { token: activeToken, currentUser, isInPreviewMode, previewStudentId };
};

// 2. Create the Provider Component
export const AuthProvider = ({ children }) => {
  const initialState = getInitialState();
  const [token, setToken] = useState(initialState.token);
  const [currentUser, setCurrentUser] = useState(initialState.currentUser);
  const [isInPreviewMode, setIsInPreviewMode] = useState(initialState.isInPreviewMode);
  const [previewStudentId, setPreviewStudentId] = useState(initialState.previewStudentId);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'authToken' || event.key === 'adminToken' || event.key === 'currentUserData') {
        console.log('Storage event detected for key:', event.key);
        const newAuthToken = localStorage.getItem('authToken');
        const newAdminToken = localStorage.getItem('adminToken');
        const newCurrentUserData = localStorage.getItem('currentUserData');

        let primaryTokenToProcess = null;

        if (event.key === 'authToken') {
          primaryTokenToProcess = newAuthToken;
          if (isValidJWT(newAuthToken) && newAuthToken !== newAdminToken) {
            localStorage.setItem('adminToken', newAuthToken); // Sync
          } else if (!isValidJWT(newAuthToken) && newAdminToken && isValidJWT(newAdminToken)) {
            primaryTokenToProcess = newAdminToken; // Fallback if authToken became invalid but adminToken is good
            localStorage.setItem('authToken', newAdminToken); // Re-sync authToken
          }
        } else if (event.key === 'adminToken') {
          primaryTokenToProcess = newAdminToken;
           if (isValidJWT(newAdminToken) && newAdminToken !== newAuthToken) {
            localStorage.setItem('authToken', newAdminToken); // Sync
          } else if (!isValidJWT(newAdminToken) && newAuthToken && isValidJWT(newAuthToken)) {
            primaryTokenToProcess = newAuthToken; // Fallback if adminToken became invalid but authToken is good
            localStorage.setItem('adminToken', newAuthToken); // Re-sync adminToken
          }
        } else if (event.key === 'currentUserData') {
            // If user data changes, ensure token state is also up-to-date with a valid token.
            // Prioritize authToken, then adminToken.
            primaryTokenToProcess = (newAuthToken && isValidJWT(newAuthToken)) ? newAuthToken : ((newAdminToken && isValidJWT(newAdminToken)) ? newAdminToken : null);
        }
        
        // If no primary token could be determined after potential sync/fallback, use current localStorage values
        if (!primaryTokenToProcess) {
            primaryTokenToProcess = (localStorage.getItem('authToken') && isValidJWT(localStorage.getItem('authToken'))) 
                                  ? localStorage.getItem('authToken') 
                                  : ((localStorage.getItem('adminToken') && isValidJWT(localStorage.getItem('adminToken'))) 
                                    ? localStorage.getItem('adminToken') 
                                    : null);
        }


        if (primaryTokenToProcess && isValidJWT(primaryTokenToProcess)) {
          setToken(primaryTokenToProcess);
          try {
            const decoded = jwtDecode(primaryTokenToProcess);
            setIsInPreviewMode(decoded.is_in_preview_mode || false);
            setPreviewStudentId(decoded.preview_student_id || null);
            
            if (newCurrentUserData) {
                try {
                    const parsedUser = JSON.parse(newCurrentUserData);
                    // Validate if current user from storage matches the token
                    if (parsedUser.id === (decoded.user_id || decoded.student_id)) {
                        setCurrentUser(parsedUser);
                    } else {
                        console.warn("Storage event: User data in storage does not match token. Re-deriving user from token or clearing.");
                        // This case should ideally be handled by handleAuthResponse setting correct data,
                        // or logout clearing it. For now, let's trust the token and clear user if mismatched severely.
                        // Or, if we have enough info in token, re-construct a minimal user.
                        // For safety, if there's a mismatch, we might need to re-evaluate.
                        // For now, if it doesn't match, let's clear currentUser to force re-auth or consistent state.
                        localStorage.removeItem('currentUserData');
                        setCurrentUser(null);
                    }
                } catch (e) {
                    console.error('Error parsing stored user data on storage event:', e);
                    localStorage.removeItem('currentUserData');
                    setCurrentUser(null);
                }
            } else if (currentUser) { // If currentUserData was removed from storage
                setCurrentUser(null);
            }

          } catch (e) {
            console.error('Error decoding token on storage event:', e, primaryTokenToProcess);
            // Token became invalid - clear everything
            localStorage.removeItem('authToken');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('currentUserData');
            setToken(null);
            setCurrentUser(null);
            setIsInPreviewMode(false);
            setPreviewStudentId(null);
          }
        } else {
          // No valid token found in storage after event
          console.log('Storage event resulted in no valid token. Clearing auth state.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('currentUserData');
          setToken(null);
          setCurrentUser(null);
          setIsInPreviewMode(false);
          setPreviewStudentId(null);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUser]); // Added currentUser to dependencies to re-evaluate if it changes externally

  const handleAuthResponse = useCallback((newToken, userApiResponse) => {
    if (!isValidJWT(newToken)) {
      console.error("handleAuthResponse received an invalid JWT:", newToken);
      // Call logout or clear state directly to prevent issues
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('currentUserData');
      setToken(null);
      setCurrentUser(null);
      setIsInPreviewMode(false);
      setPreviewStudentId(null);
      return;
    }

    try {
      const decoded = jwtDecode(newToken);
      console.log('Decoded JWT on auth response:', decoded);

      let userData = {
        id: decoded.user_id || decoded.student_id,
        email: decoded.email,
        firstName: decoded.first_name || userApiResponse?.firstName,
        lastName: decoded.last_name || userApiResponse?.lastName,
        is_local: decoded.is_local,
        role: decoded.student_id && !decoded.is_in_preview_mode ? 'student' : 'educator',
        isAdmin: decoded.is_admin === true,
      };

      // If userApiResponse contains a nested user object (e.g., from credentials login)
      // or if userApiResponse itself is the user object (e.g. from brokered login where token is primary)
      const apiUser = userApiResponse?.user || userApiResponse;

      if (apiUser && typeof apiUser === 'object') {
        userData.id = apiUser.id || userData.id;
        userData.email = apiUser.email || userData.email;
        // Prefer direct properties, then snake_case from token as fallback
        userData.firstName = apiUser.firstName || apiUser.first_name || userData.firstName;
        userData.lastName = apiUser.lastName || apiUser.last_name || userData.lastName;
        userData.isAdmin = apiUser.isAdmin !== undefined ? apiUser.isAdmin : userData.isAdmin;
        if (apiUser.id && !decoded.student_id) { // If API provides user.id and it's not a student token
            userData.role = 'educator';
        }
      }
        
      localStorage.setItem('authToken', newToken);
      localStorage.setItem('adminToken', newToken); // Keep adminToken in sync
      localStorage.setItem('currentUserData', JSON.stringify(userData));
        
      setToken(newToken);
      setCurrentUser(userData);
      setIsInPreviewMode(decoded.is_in_preview_mode || false);
      setPreviewStudentId(decoded.preview_student_id || null);

      console.log(`Auth state updated. Role: ${userData.role}, Admin: ${userData.isAdmin}, Preview: ${isInPreviewMode}`, userData);
    } catch (e) {
      console.error("Error processing auth response (even with valid JWT structure):", e, newToken);
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('currentUserData');
      setToken(null);
      setCurrentUser(null);
      setIsInPreviewMode(false);
      setPreviewStudentId(null);
    }
  }, []); // Removed isInPreviewMode from dependencies as it's derived from token

  const login = useCallback((newToken, userOrStudentObjectFromApi) => {
    // Ensure the token from login is structurally valid before processing
    if (!isValidJWT(newToken)) {
        console.error('Login attempt with invalid JWT structure:', newToken);
        // Potentially set an error state or simply don't proceed
        // For now, we'll let handleAuthResponse deal with it, which also checks.
    }
    handleAuthResponse(newToken, userOrStudentObjectFromApi);
  }, [handleAuthResponse]);

  const logout = useCallback(() => {
    console.log("User logging out. Clearing all auth data.");
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('currentUserData');
    setToken(null);
    setCurrentUser(null);
    setIsInPreviewMode(false);
    setPreviewStudentId(null);
    setShowLoginModal(false);
    setLoginRedirectPath(null);
  }, []);

  const enterPreviewMode = useCallback(async () => {
    if (!token || !isValidJWT(token)) {
        console.warn("Enter preview mode: No valid token available.");
        return;
    }
    try {
      const response = await axios.post('/api/auth/teacher/enter-preview', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.token) {
        // The token from preview mode API should also be validated
        if (!isValidJWT(response.data.token)) {
            console.error("Enter preview mode: API returned an invalid JWT.", response.data.token);
            // Optionally logout or show error
            logout(); 
            return;
        }
        handleAuthResponse(response.data.token, response.data.user || {}); // Pass user data if available
      } else {
        console.warn("Enter preview mode: API response did not contain a token.", response.data);
      }
    } catch (error) {
      console.error('Error entering preview mode:', error.response?.data?.error || error.message);
    }
  }, [token, handleAuthResponse, logout]);

  const exitPreviewMode = useCallback(async () => {
    if (!token || !isValidJWT(token)) {
        console.warn("Exit preview mode: No valid token available.");
        return;
    }
    try {
      const response = await axios.post('/api/auth/teacher/exit-preview', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.token) {
         if (!isValidJWT(response.data.token)) {
            console.error("Exit preview mode: API returned an invalid JWT.", response.data.token);
            logout();
            return;
        }
        handleAuthResponse(response.data.token, response.data.user || {}); // Pass user data if available
      } else {
        console.warn("Exit preview mode: API response did not contain a token.", response.data);
      }
    } catch (error) {
      console.error('Error exiting preview mode:', error.response?.data?.error || error.message);
    }
  }, [token, handleAuthResponse, logout]);

  const isLoggedIn = !!token && isValidJWT(token) && !!currentUser; // Ensure token is also structurally valid for loggedIn status
  const isAdmin = currentUser?.isAdmin === true;
  const isStudent = currentUser?.role === 'student' && !isInPreviewMode;
  const userEmail = currentUser?.email;
  const isLocalAuth = currentUser?.is_local;
  
  const [isLoading, setIsLoading] = useState(true); // isLoading for initial auth check
  
  // Global URL token extraction - runs once on app initialization
  useEffect(() => {
    const extractTokenFromURL = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      const authSuccess = urlParams.get('auth_success');
      const alreadyAuth = urlParams.get('already_auth');
      
      if (urlToken && (authSuccess === 'true' || alreadyAuth === 'true')) {
        console.log('AuthContext: Found authentication token in URL, processing...');
        try {
          // Validate token structure
          if (isValidJWT(urlToken)) {
            const tokenParts = urlToken.split('.');
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('AuthContext: Decoded token payload:', payload);
            
            // Use handleAuthResponse to store the token properly
            handleAuthResponse(urlToken, payload);
            
            // Delay URL cleanup to allow LayoutContext to detect parameters first
            setTimeout(() => {
              const cleanUrl = new URL(window.location);
              cleanUrl.searchParams.delete('token');
              cleanUrl.searchParams.delete('auth_success');
              cleanUrl.searchParams.delete('already_auth');
              
              // Update URL without causing a page reload
              window.history.replaceState(null, '', cleanUrl.toString());
            }, 500); // 500ms delay to allow layout detection
            
            console.log('AuthContext: Authentication successful, URL cleaned');
          } else {
            console.error('AuthContext: Invalid token format from URL');
          }
        } catch (error) {
          console.error('AuthContext: Error processing URL token:', error);
        }
      }
    };

    // Extract token from URL first
    extractTokenFromURL();
    
    // Then handle initial token validation
    if (initialState.token && !isValidJWT(initialState.token)) {
        console.log("Initial token was invalid, forcing logout path in isLoading effect.");
        logout(); // Ensure clean state if initial token was bad
    }
    setIsLoading(false);
  }, [initialState.token, logout, handleAuthResponse]); // Added logout to dependencies

  const value = {
    token,
    currentUser,
    userEmail,
    isLoggedIn,
    isLocalAuth,
    isAdmin,
    isStudent,
    login,
    logout,
    showLoginModal,
    setShowLoginModal,
    loginRedirectPath,
    setLoginRedirectPath,
    isInPreviewMode,
    previewStudentId,
    enterPreviewMode,
    exitPreviewMode,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined || context === null) { 
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 